import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { JournalCarte } from '../../../utils/types/JournalCarte';
import * as XLSX from 'xlsx';
import { ReleveClientDTO } from '../../../utils/types/objects/ReleveClientDTO';

ModuleRegistry.registerModules([AllCommunityModule]);

interface ReleveClientGridProps {
    items: ReleveClientDTO[];
}

const ReleveClientGrid: React.FC<ReleveClientGridProps> = ({ items }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<ReleveClientDTO>[]>([
        {
            field: 'dateOperation',
            headerName: 'Date',
            valueFormatter: (params) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy') : ''),
        },

        {
            field: 'typeOperation',
            headerName: 'Type Opération',
        },

        {
            field: 'libelle',
            headerName: 'Libellé',
        },

        {
            field: 'debit',
            headerName: 'Débit (USD)',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';

                return new Intl.NumberFormat('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(value);
            },
            cellStyle: { textAlign: 'right' },
        },

        {
            field: 'credit',
            headerName: 'Crédit (USD)',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';

                return new Intl.NumberFormat('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(value);
            },
            cellStyle: { textAlign: 'right' },
        },

        {
            field: 'solde',
            headerName: 'Solde (USD)',
            valueFormatter: (params) => {
                const value = params.value;
                if (value == null) return '';

                return new Intl.NumberFormat('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(value);
            },
            cellStyle: { textAlign: 'right' },
        },

        {
            field: 'notes',
            headerName: 'Notes',
        },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `releve_client_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
            processCellCallback: (params) => {
                const field = params.column.getColDef().field;
                const value = params.value;

                if (field === 'dateOperation') {
                    return value ? format(new Date(value), 'dd/MM/yyyy') : '';
                }

                return value ?? '';
            },
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: JournalCarte[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const rowObj: Record<string, any> = {};
            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                const header = colDef.headerName ?? field ?? '';
                if (!field) return;

                let value = (row as any)[field];
                if (field === 'dateOperation') {
                    value = value ? format(new Date(value), 'dd/MM/yyyy') : '';
                }

                rowObj[header] = value;
            });
            return rowObj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ReleveClient');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `releve_client_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        theme={myTheme}
                        rowData={items}
                        columnDefs={columnDefs}
                        domLayout="normal"
                        defaultColDef={{
                            resizable: true,
                            editable: false,
                            minWidth: 100,
                            flex: 1,
                            sortable: false,
                            filter: false,
                            floatingFilter: false,
                        }}
                        suppressExcelExport={true}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        animateRows={true}
                        suppressMovableColumns={true}
                        suppressColumnVirtualisation={false}
                        overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                    />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default ReleveClientGrid;
